// src/v2/components/ToolbarAddButton.jsx
//
// TOOLBAR-CONTROL-HEIGHT-NORMALIZE-S1 — single shared component for all
// "+Add" buttons in page toolbars.
//
// Use variant="primary" for collection primary add actions (Places/Offices,
// Leases, Projects). Keep this single specification for future collection adds;
// no per-page sizing overrides. The default preserves existing Market Plan
// actions, which are outside the collection-primary styling scope.
//
// Using one component instead of per-page hand-rolled buttons guarantees
// the rendered height is always T.toolbarCtrlH, preventing the style drift
// where a raw <button> with a different fontSize or border ended up 14px
// taller than its siblings and shifted the vertically-centered search bar.
//
// Props:
//   testId    — data-testid placed on the <button>
//   onClick   — click handler (optional; button renders but is a no-op when omitted)
//   children  — label only for primary; arbitrary content for default
//   variant   — "primary" opts into the approved collection typography/box model

import React, { useState } from "react";
import { T } from "../../app/tokens.js";

const PRIMARY_ACTION_STYLE = {
  width: 128,
  justifyContent: "center",
  fontWeight: T.fwSemibold,
  fontFamily: "Arial",
  lineHeight: 1,
  boxSizing: "border-box",
};

export function ToolbarAddButton({ testId, onClick, children, variant, "aria-label": ariaLabel }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        gap:          5,
        background:   "var(--v2-accent)",
        color:        T.white,
        border:       0,
        borderRadius: 9,
        padding:      "0 14px",
        height:       T.toolbarCtrlH,
        fontSize:     T.fsSm5,
        fontWeight:   T.fwBold,
        ...(variant === "primary" ? PRIMARY_ACTION_STYLE : {}),
        cursor:       "pointer",
        whiteSpace:   "nowrap",
        flexShrink:   0,
        transform:    hovered ? "scale(1.02)" : "scale(1)",
        filter:       hovered ? "brightness(1.08)" : "none",
        transition:   "transform 0.12s, filter 0.12s",
      }}
    >
      {variant === "primary" && (
        <svg data-toolbar-add-plus="" aria-hidden="true" focusable="false"
          width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
          style={{ flexShrink: 0 }}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      )}
      {children}
    </button>
  );
}
